<template>
  <div
    class="samples-filter mb-5"
    @keyup.enter="submitSearch"
  >
    <card
      title="Filtros"
      class="mb-5"
    >
      <template #headerAction>
        <div class="filter-header-pending-samples">
          <checkbox
            v-model="filter.onlyPending"
            label="Somente pendentes"
          />
        </div>
      </template>

      <div class="mb-5">
        <md-layout class="gap-4">
          <search-field-selector
            class="search-date-select-options"
            :selected-option="selectedDateOption"
            :text-value="dateQuery"
            :date-range-value="dateSelected"
            :default-start-date="defaultStartDate"
            :default-end-date="defaultEndDate"
            :clean-date="cleanDate"
            :limit-date="limitDate"
            :options="dateSelectOptions"
            :default-option-index="defaultOptionIndexDate"
            @updateSearchValue="setDate"
            @enterPressed="submitSearch"
          />

          <search-field-selector
            class="search-multiple-text"
            :default-option-index="defaultOptionIndexText"
            :selected-option="selectedTextOption"
            :text-value="textQuery"
            :options="selectOptions"
            @update:textValue="textQuery = $event"
            @updateSearchValue="setMultipleTextSearch"
            @enterPressed="submitSearch"
          />

          <md-button
            class="btn btn-nonenary"
            @click="submitSearch"
          >
            <svg-loader src="/static/images/icon-generate-list.svg" />

            Listar
          </md-button>
        </md-layout>
      </div>

      <md-layout class="gap-2 justify-between mb-2">
        <batch-packaging-select v-model="packagingSelected" />

        <div>
          <md-layout class="gap-4">
            <md-radio
              v-for="option in radioOptions"
              :key="option.value"
              v-model="printedRoot"
              :md-value="option.value"
              class="radio"
            >
              {{ option.label }}
            </md-radio>
          </md-layout>
        </div>
      </md-layout>

      <div
        class="more-filters"
        :class="{ 'more-filters-open': showMoreFilters }"
      >
        <md-button
          class="more-filters-button"
          @click="toggleMoreFilters"
        >
          Mais filtros

          <md-icon
            md-src="/static/images/arrow-up-primary-color.svg"
            class="more-filters-button-icon"
          />
        </md-button>

        <transition name="dropdown">
          <div v-show="showMoreFilters">
            <div>
              <p class="mb-2">Origem da OS:</p>

              <md-layout class="gap-4">
                <md-radio
                  v-for="(option, index) in osOriginOptions"
                  :key="index"
                  v-model="filter.source"
                  :md-value="option.value"
                  class="mb-0"
                >
                  {{ option.label }}
                </md-radio>
              </md-layout>
            </div>

            <md-layout
              v-if="filter.source === 'AOL2'"
              class="gap-2 align-end mt-4"
            >
              <div>
                <p class="mb-2">Selecione por tipo de cadastro (Apenas AOL):</p>

                <md-layout class="gap-4">
                  <md-radio
                    v-for="option in typeRegisterOptions"
                    :key="option.value"
                    v-model="integratedRoot"
                    :md-value="option.value"
                    class="mb-0"
                    @input="onGetFiles"
                  >
                    {{ option.label }}
                  </md-radio>
                </md-layout>
              </div>

              <md-layout v-if="integratedRoot">
                <field-container label="Arquivos (XML)">
                  <div>
                    <div v-if="filesSentStatus === 'STATUS_END'">
                      <model-list-select
                        id="comboFiles"
                        v-model="file"
                        v-validate="'required'"
                        option-text="name"
                        placeholder="Selecione um arquivo"
                        :options="getAvailableFiles()"
                        option-value="id_file"
                        :disabled="!dateSelected"
                      >
                        <span slot="no-options">
                          Não foram encontrados arquivos com OS's transferidas para o
                          período selecionado.
                        </span>
                      </model-list-select>
                    </div>

                    <div v-else>
                      <spinner class="small search__loading" />
                    </div>
                  </div>
                </field-container>
              </md-layout>
            </md-layout>
          </div>
        </transition>
      </div>
    </card>

    <div class="samples-filter-header">
      <label-data-title
        label="Listagem de amostras"
        :data="currentFilterLabel"
      />

      <div class="samples-filter-header-buttons">
        <button
          class="btn btn-primary"
          :disabled="!isPrintReceiptEnabled"
          @click="loadingSamplesReceipt"
        >
          <svg-loader
            class="icon-print"
            src="/static/images/icon-print-alternative-white.svg"
          />

          IMPRIMIR RECIBO

          <span v-if="selectedSamples.length > 0">({{ selectedSamples.length }})</span>
        </button>

        <button
          class="btn btn-primary"
          :disabled="!hasSamples"
          @click="handlePrintSamples"
        >
          <svg-loader
            class="icon-print"
            src="/static/images/icon-print-alternative-white.svg"
          />

          IMPRIMIR ETIQUETAS

          <span v-if="selectedSamples.length > 0">({{ selectedSamples.length }})</span>
        </button>
      </div>
    </div>

    <modal
      ref="loadingReceiptModal"
      template="loading"
      title="Aguarde"
      ref-name="loadingReceiptModal"
    >
      <p>Gerando recibo...</p>
    </modal>

    <PrinterModal ref="printerModal" />

    <sample-modal-confirmation
      ref="samplesQuestionModal"
      @printTags="print"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

import { formatDate } from '@/utils/date-utils'
import { arePrintFiltersEqual, persistPrintFilters, restorePrintFilters } from '@/utils/print-filter-session'

import {
  BATCH_PACKAGING_FROZEN_ID,
  BATCH_PACKAGING_REFRIGERATED_DRY_ID,
  BATCH_PACKAGING_REFRIGERATED_ID,
  BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
} from '@/enums'

import notify from '@/api/notification'
import { eventBus, OPEN_PRINTER_PLUGIN_MODAL } from '@/api/eventbus'

import Card from '@/components/common/Card'
import ButtonIcon from '@/components/common/ButtonIcon'
import SearchFieldSelector from '@/components/common/SearchFieldSelectorV2'
import Spinner from '@/components/common/Spinner'
import FieldContainer from '@/components/common/FieldContainer'
import SelectSearch from '@/components/common/SelectSearch'
import BatchPackagingSelect from '@/components/common/BatchPackagingSelect'
import LabelDataTitle from '@/components/common/LabelDataTitle'
import Checkbox from '@/components/common/Checkbox'
import PrinterModal from '@/components/common/printer/PrinterModal'

import SamplesPrintButtons from './SamplesPrintButtons'
import SampleModalConfirmation from './SampleModalConfirmation'

export default {
  name: 'SamplesFilter',
  components: {
    Card,
    ButtonIcon,
    SearchFieldSelector,
    Spinner,
    FieldContainer,
    SelectSearch,
    BatchPackagingSelect,
    LabelDataTitle,
    SamplesPrintButtons,
    Checkbox,
    PrinterModal,
    SampleModalConfirmation,
  },
  data() {
    return {
      today: new Date(),
      cleanDate: 0,
      filter: {
        source: '',
        onlyPending: false,
      },
      activeFilter: null,
      typeDateSelected: null,
      dateSelected: null,
      limitDaysSamples: 20,
      patientNameRoot: null,
      codeOriginAlvaroRoot: null,
      codeOriginLisRoot: null,
      qRoot: null,
      batchLabelRoot: null,
      packagingSelected: [
        BATCH_PACKAGING_FROZEN_ID,
        BATCH_PACKAGING_REFRIGERATED_ID,
        BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
        BATCH_PACKAGING_REFRIGERATED_DRY_ID,
      ],
      file: null,
      filesAvailables: [],
      integratedRoot: '',
      printedRoot: '',
      showMoreFilters: false,
      filtersInitialized: false,
      osOriginOptions: [
        {
          value: '',
          label: 'Todas',
        },
        {
          value: 'AOL2',
          label: 'AOL',
        },
        {
          value: 'AOLWS',
          label: 'LIS',
        },
      ],
      dateSelectOptions: [
        {
          id: 'transferDate',
          itemValue: 'TD',
          label: 'Data da transferência',
          placeholder: 'Data da transferência',
          limitDays: 1827,
          type: 'date',
        },
        {
          id: 'collectDate',
          itemValue: 'CD',
          label: 'Data da coleta',
          placeholder: 'Data da coleta',
          limitDays: 1827,
          type: 'date',
        },
      ],
      selectOptions: [
        {
          id: 'patientName',
          label: 'Nome do Paciente',
          placeholder: 'Nome do Paciente',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'codeOriginAlvaro',
          label: 'OS Alvaro',
          placeholder: 'Os Alvaro',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'codeOriginLis',
          label: 'OS Lis',
          placeholder: 'OS Lis',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'q',
          label: 'Amostra',
          placeholder: 'Amostra',
          limitDays: null,
          singleDate: false,
        },
      ],
      radioOptions: [
        { value: '', label: 'Todas' },
        { value: true, label: 'Impressas' },
        { value: false, label: 'Não impressas' },
      ],
      typeRegisterOptions: [
        { value: '', label: 'Todos' },
        { value: false, label: 'Manual' },
        { value: true, label: 'Via importação' },
      ],
      selectedDateOption: null,
      dateQuery: '',
      selectedTextOption: null,
      textQuery: '',
      defaultOptionIndex: 0,
      defaultOptionIndexDate: 0,
      defaultOptionIndexText: 0,
      processingOrigin: null,
      idRecollect: null,
      isRecollectRedirect: false,
      showOnlyPendingSamples: false,
    }
  },
  computed: {
    ...mapGetters([
      'filesUploaded',
      'filesSentStatus',
      'profile',
      'samples',
      'webSocketStatus',
      'selectedSamples',
      'toggle',
      'defaultPrinter',
    ]),
    isPrintReceiptEnabled() {
      return (
        this.hasSamples && !this.selectedSamples.some(sample => sample.origem === 'LIS')
      )
    },
    hasSamples() {
      return this.samples.length > 0
    },
    currentFilterLabel() {
      const formattedStartDate = moment(
        this.activeFilter?.dateSelected?.start ?? this.today
      ).format('DD/MM/YYYY')
      const formattedEndDate = moment(
        this.activeFilter?.dateSelected?.end ?? this.today
      ).format('DD/MM/YYYY')

      let formattedDate =
        formattedStartDate === formattedEndDate
          ? formattedStartDate
          : `${formattedStartDate} até ${formattedEndDate}`

      if (formattedDate === moment(this.today).format('DD/MM/YYYY')) {
        formattedDate = 'hoje'
      }

      return `de ${formattedDate} (${this.samples.length})`
    },
    defaultStartDate() {
      return this.dateSelected?.start
    },

    defaultEndDate() {
      return this.dateSelected?.end
    },

    sampleFilter() {
      const disabledRangeDate = this.idRecollect

      return {
        startDate: disabledRangeDate ? undefined : this.startDate,
        endDate: disabledRangeDate ? undefined : this.endDate,
        dateType: disabledRangeDate ? undefined : this.typeDateSelected,
        patientName: this.patientNameRoot ? this.patientNameRoot : undefined,
        codeOriginLis: this.codeOriginLisRoot ? this.codeOriginLisRoot : undefined,
        codeOriginAlvaro: this.codeOriginAlvaroRoot
          ? this.codeOriginAlvaroRoot
          : undefined,
        q: this.qRoot ? this.qRoot : undefined,
        packagingType: this.packagingSelected,
        integrated: this.filter.source === 'AOL2' ? this.integratedRoot : undefined,
        printed: this.printedRoot,
        idFile:
          this.filter.source === 'AOL2' && this.showMoreFilters && this.integratedRoot
            ? (this.file?.id_file ?? undefined)
            : undefined,
        processingOrigin: this.processingOrigin,
        idRecollect: this.idRecollect,
        source: this.filter.source ? this.filter.source : undefined,
        sampleStatus: this.filter.onlyPending ? 1 : undefined,
      }
    },
    sessionFilter() {
      return {
        ...this.sampleFilter,
        startDate: this.startDate,
        endDate: this.endDate,
        dateType: this.typeDateSelected,
        packagingType: [...this.packagingSelected],
        patientName: this.patientNameRoot || '',
        codeOriginLis: this.codeOriginLisRoot || '',
        codeOriginAlvaro: this.codeOriginAlvaroRoot || '',
        q: this.qRoot || '',
        source: this.filter.source,
        sampleStatus: this.filter.onlyPending ? 1 : '',
        integrated: this.integratedRoot,
        idFile: this.file?.id_file || '',
        textField: this.selectedTextOption?.id || this.selectOptions[this.defaultOptionIndexText]?.id,
        showMoreFilters: this.showMoreFilters,
      }
    },

    limitDate() {
      return moment(this.today, 'DD/MM/YYYY')
        .subtract(this.limitDaysSamples, 'days')
        .toDate()
    },

    startDate() {
      return formatDate(this.dateSelected?.start, 4)
    },

    endDate() {
      return formatDate(this.dateSelected?.end, 4)
    },
  },
  watch: {
    '$route.query': {
      handler(query) {
        return this.restoreQueryFilters(query)
      },
    },
    dateSelected(newValue) {
      if (newValue) {
        this.dateQuery = `${newValue.start} - ${newValue.end}`
      }
    },
    'filter.source'(newValue) {
      if (!this.filtersInitialized) return
      if (newValue !== 'AOL2') {
        this.integratedRoot = ''
        this.file = null
      }
      this.submitSearch()
    },
    'filter.onlyPending'() {
      if (!this.filtersInitialized) return
      this.submitSearch()
    },
    packagingSelected() {
      if (!this.filtersInitialized) return
      this.submitSearch()
    },
  },
  created() {
    this.setInitialFilters()
  },
  async mounted() {
    await this.$nextTick()
    this.filtersInitialized = true
    await this.submitSearch()
  },
  methods: {
    ...mapActions([
      'selectSampleList',
      'getServiceSample',
      'getFilesSent',
      'printReceipt',
      'printSamples',
      'webSocketPrintSamples',
      'syncPrinterFromStorage',
    ]),
    async restoreQueryFilters(query) {
      if (!/\/print-tag\/samples\/?$/.test(this.$route.path)) return
      if (!this.filtersInitialized && !this.hydratingFilters) return
      if (arePrintFiltersEqual(query, this.lastAppliedQuery || {})) return
      const generation = (this.filterHydrationGeneration || 0) + 1
      this.filterHydrationGeneration = generation
      this.hydratingFilters = true
      this.filtersInitialized = false
      this.setInitialFilters()
      await this.$nextTick()
      if (generation !== this.filterHydrationGeneration) return
      this.hydratingFilters = false
      this.filtersInitialized = true
      return this.submitSearch()
    },
    setInitialFilters() {
      this.typeDateSelected = 'TD'
      this.dateSelected = { start: this.today, end: this.today }
      this.defaultOptionIndexDate = 0
      this.defaultOptionIndexText = 0
      this.patientNameRoot = null
      this.codeOriginAlvaroRoot = null
      this.codeOriginLisRoot = null
      this.qRoot = null
      this.batchLabelRoot = null
      this.textQuery = ''
      this.integratedRoot = ''
      this.printedRoot = ''
      this.file = null
      this.showMoreFilters = false
      this.processingOrigin = null
      this.idRecollect = null
      this.isRecollectRedirect = false
      this.packagingSelected = [
        BATCH_PACKAGING_FROZEN_ID,
        BATCH_PACKAGING_REFRIGERATED_ID,
        BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
        BATCH_PACKAGING_REFRIGERATED_DRY_ID,
      ]

      this.setFiltersFromUrl()
    },
    setFiltersFromUrl() {
      const query = restorePrintFilters('samples', this.$route.query)

      if (query.startDate !== undefined || query.endDate !== undefined) {
        this.dateSelected = {
          start: query.startDate ? this.parseDate(query.startDate) : null,
          end: query.endDate ? this.parseDate(query.endDate) : null,
        }
      }

      if (query.dateType) {
        this.typeDateSelected = query.dateType
        this.defaultOptionIndexDate = query.dateType === 'CD' ? 1 : 0
      }
      this.selectedDateOption = this.dateSelectOptions[this.defaultOptionIndexDate]

      if (query.codeOriginAlvaro) {
        this.codeOriginAlvaroRoot = query.codeOriginAlvaro
        this.textQuery = query.codeOriginAlvaro
        this.defaultOptionIndexText = 1
      }

      if (query.codeOriginLis) {
        this.codeOriginLisRoot = query.codeOriginLis
        this.textQuery = query.codeOriginLis
        this.defaultOptionIndexText = 2
      }

      if (query.patientName) {
        this.patientNameRoot = query.patientName
        this.textQuery = query.patientName
        this.defaultOptionIndexText = 0
      }

      if (query.q) {
        this.qRoot = query.q
        this.textQuery = query.q
        this.defaultOptionIndexText = 3
      }

      if (query.textField) {
        const index = this.selectOptions.findIndex(option => option.id === query.textField)
        if (index !== -1) this.defaultOptionIndexText = index
      }
      this.selectedTextOption = this.selectOptions[this.defaultOptionIndexText]
      this.filter.source = query.source || ''
      this.filter.onlyPending = String(query.sampleStatus) === '1'

      if (query.integrated !== undefined && query.integrated !== '') {
        this.integratedRoot = query.integrated === true || query.integrated === 'true'
        this.showMoreFilters = true
      }

      if (query.printed !== undefined && query.printed !== '') {
        this.printedRoot = query.printed === true || query.printed === 'true'
      }

      if (query.packagingType !== undefined) {
        this.packagingSelected = (Array.isArray(query.packagingType)
          ? query.packagingType
          : String(query.packagingType).split(','))
          .filter(value => value !== '')
          .map(Number)
      }

      if (query.idFile) {
        this.file = this.getAvailableFiles().find(file => String(file.id_file) === String(query.idFile)) ||
          { id_file: query.idFile }
      }
      if (query.showMoreFilters !== undefined) {
        this.showMoreFilters = query.showMoreFilters === true || query.showMoreFilters === 'true'
      }

      if (query.processingOrigin) {
        this.processingOrigin = query.processingOrigin
      }

      if (query.idRecollect) {
        this.idRecollect = query.idRecollect
        this.isRecollectRedirect = true
      }
    },
    setDate(e) {
      const { itemValue, date } = e || {}
      if (date && !date.start && !date.end) {
        this.dateSelected = { start: null, end: null }
        return
      }
      if (!date?.start || !date?.end) return

      const maxDays = 21
      const startDate = moment(date.start)
      const endDate = moment(date.end)
      if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(startDate, 'day')) return
      const diffDays = endDate.diff(startDate, 'days')

      if (diffDays > maxDays) {
        notify({
          title: 'AVISO:',
          message: `Selecione um período de até ${maxDays} dias.`,
        })
        this.dateSelected = { ...this.dateSelected }
        return
      }
      const changed = !startDate.isSame(this.dateSelected?.start, 'day') ||
        !endDate.isSame(this.dateSelected?.end, 'day') ||
        (itemValue && itemValue !== this.typeDateSelected)
      this.selectedDateOption = this.dateSelectOptions.find(option => option.itemValue === itemValue) || this.selectedDateOption
      this.typeDateSelected = itemValue || this.typeDateSelected
      this.dateSelected = { start: startDate.toDate(), end: endDate.toDate() }
      if (changed && this.filtersInitialized) return this.submitSearch()
    },
    setMultipleTextSearch(e) {
      this.selectedTextOption = e
      const query = e?.query?.trim() || ''
      this.textQuery = query

      this.patientNameRoot = ''
      this.codeOriginAlvaroRoot = ''
      this.codeOriginLisRoot = ''
      this.qRoot = ''
      this.batchLabelRoot = ''

      switch (e?.id) {
        case 'patientName':
          this.patientNameRoot = query
          break
        case 'codeOriginAlvaro':
          this.codeOriginAlvaroRoot = query
          break
        case 'codeOriginLis':
          this.codeOriginLisRoot = query
          break
        case 'q':
          this.qRoot = query
          break
      }
    },
    async submitSearch() {
      const path = this.$route.path
      await this.$nextTick()
      if (this._isDestroyed || this._isBeingDestroyed || this.$route.path !== path) return
      const searchKey = JSON.stringify(this.sessionFilter)
      if (this.searchPromise && this.searchKey === searchKey) return this.searchPromise
      this.searchKey = searchKey
      const generation = (this.searchGeneration || 0) + 1
      this.searchGeneration = generation
      this.lastAppliedQuery = { tab: this.$route.query.tab, ...this.sessionFilter }
      const filters = { ...this.sampleFilter, packagingType: [...this.packagingSelected] }
      const search = async () => {
        this.activeFilter = { dateSelected: { ...this.dateSelected } }
        await persistPrintFilters(this, 'samples', this.sessionFilter)
        if (this._isDestroyed || this._isBeingDestroyed || this.$route.path !== path || this.searchGeneration !== generation) return
        this.selectSampleList(filters)
        await this.getServiceSample()
        if (this._isDestroyed || this._isBeingDestroyed || this.$route.path !== path || this.searchGeneration !== generation) return
        await this.getFiles()
      }
      this.searchPromise = search()
      try {
        await this.searchPromise
      } finally {
        if (this.searchKey === searchKey) this.searchPromise = null
      }
    },
    async getFiles() {
      const params = {
        startDate: this.startDate,
        endDate: this.endDate,
        idLaboratory: this.profile?.id_laboratory,
        stateList: 'TRANSMITTED',
      }
      await this.getFilesSent(params)
      this.filesAvailables = this.getAvailableFiles()
      if (this.file) {
        this.file = this.filesAvailables.find(file => String(file.id_file) === String(this.file.id_file)) || this.file
      }
    },
    getAvailableFiles() {
      if (!this.filesUploaded) {
        return []
      }
      const clonedFilesArray = this.filesUploaded.slice(0)
      clonedFilesArray.map(file => {
        file.label = file.name
        file.value = file.id_file
      })
      return clonedFilesArray
    },
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },
    onGetFiles() {
      if (!this.integratedRoot) return
      this.getFiles()
    },
    parseDate(dateStr) {
      const parsed = moment(dateStr, ['DD/MM/YYYY', moment.ISO_8601], true)
      return parsed.isValid() ? parsed.toDate() : null
    },
    async loadingSamplesReceipt() {
      this.$refs.loadingReceiptModal.open()

      const samplesToPrint =
        this.selectedSamples?.length > 0 ? this.selectedSamples : this.samples
      const samplesToPrintIds = samplesToPrint
        .map(sample => sample.sample_id)
        .filter(Boolean)

      await this.printReceipt(samplesToPrintIds)
      this.$refs.loadingReceiptModal.close()
      this.pushEventToAnalytics('Impressão de recibo de amostra')
    },
    pushEventToAnalytics(action) {
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Impressão de Etiquetas - Amostras',
        action,
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName,
      })
    },
    async handlePrintSamples() {
      await this.executeWithPrinterValidation(() => {
        const samplesToPrint =
          this.selectedSamples?.length > 0 ? this.selectedSamples : this.samples
        const barcodeList = [
          ...new Set(samplesToPrint.map(sample => sample.bar_code).filter(Boolean)),
        ]
        const batchList = [
          ...new Set(
            samplesToPrint.map(sample => sample.batch_identifier).filter(Boolean)
          ),
        ]
        const payload = samplesToPrint.map(sample => ({
          sample_id: sample.sample_id,
          bar_code: sample.bar_code,
          batch: {
            label: sample.batch_label,
            id: sample.batch_id ?? sample.batch_identifier,
          },
          batchList,
          barcodeList,
        }))

        const isThereSomeBatchToClose = samplesToPrint.some(
          sample => sample.batch_identifier
        )

        if (isThereSomeBatchToClose) {
          this.$refs.samplesQuestionModal.openModalConfirmation(payload)
        } else {
          this.print('yes', payload)
        }
      })
    },
    openConfigModal() {
      this.$refs.printerModal.open()
    },
    hasValidPrinter() {
      this.syncPrinterFromStorage()

      return !!this.defaultPrinter
    },
    async executeWithPrinterValidation(action) {
      const isWebSocketConnected = this.webSocketStatus?.connectionState === 'connected'

      if (!isWebSocketConnected) {
        eventBus.$emit(OPEN_PRINTER_PLUGIN_MODAL)
        return
      }

      const hasValidPrinter = this.hasValidPrinter()

      if (hasValidPrinter) {
        await this.$nextTick()
        action()
      } else {
        this.openConfigModal()
      }
    },
    async print(answer, samplesToPrint) {
      try {
        if (this.toggle.printerPlugin) {
          await this.webSocketPrintSamples({
            samplesId: samplesToPrint,
            printBatch: answer === 'yes',
            batch: {},
          })

          await this.getServiceSample()
        } else {
          this.printSamples({
            samplesId: samplesToPrint,
            printBatch: answer === 'yes',
            source: this.filter.source || undefined,
          })
        }
      } catch (error) {
        console.error('Erro na impressão:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

.samples-filter {
  .more-filters {
    &-button {
      margin: 0;
      padding: 0;
      background-color: transparent;
      color: $theme-primary-color;
      min-height: auto;
      height: auto;

      &:hover {
        background-color: transparent;
      }

      &-icon {
        transform: rotate(180deg);
        transition: transform 0.3s ease-in-out;
      }

      .md-icon {
        min-width: auto;
        min-height: auto;
        width: 1rem;
        height: 1rem;
      }

      ::v-deep(.md-ink-ripple) {
        display: none !important;
      }
    }

    &.more-filters-open .more-filters-button-icon {
      transform: rotate(0deg) !important;
    }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-family: 'Exo 2';
    font-size: 0.75rem;
    font-weight: 600;
    height: 40px;
    line-height: 0;
    text-transform: uppercase;
  }

  .md-radio {
    margin: 0;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    &-buttons {
      display: flex;
      gap: 1rem;

      .btn {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        font-family: 'Exo 2';
        font-size: 0.75rem;
        font-weight: 600;
        height: 40px;
        line-height: 0;
        text-transform: uppercase;

        .md-icon {
          min-width: auto;
          min-height: auto;
          width: 1rem;
          height: 1rem;
        }
      }
    }
  }
}

:deep(.search .search-field-selector) {
  height: 40px;
  min-height: auto;
}

:deep(.md-theme-default.md-radio .md-radio-container:after) {
  background-color: $theme-primary-color !important;
}

:deep(.md-theme-default.md-radio.md-checked .md-radio-container) {
  border-color: $theme-primary-color !important;
}

.icon-filter {
  color: $theme-primary-color;
}

.search-date-select-options,
.search-multiple-text {
  width: auto;
  flex: 1;
  margin: 0;
}

.container-file {
  width: 40%;
}

.filter-header-pending-samples {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding-left: 2rem;
}

::v-deep(.card-form__container .title-main) {
  color: $theme-primary-color;
}
</style>
